// chat/actions.ts — the chat control executor (cloned from Oriora Expense's src/chat/actions.ts,
// adapted to THIS app's store). The client router (chat/brain.ts) maps ONE user message → ONE
// ChatAction; this file EXECUTES that action against the on-device store (src/store.ts). Everything
// here is LOCAL SQLite over Tauri IPC — no network, no billing — EXCEPT `research`, which reuses the
// app's ONE existing live pipeline (runResearch = plan → gather → synthesize) exactly as Home's
// runResearchFlow does. No new server routes, no new endpoints.
//
// HITL discipline (mirrors Expense/Repurchase): the destructive actions (delete_brief, clear_history)
// are NEVER executed from a chat turn on their own — the Chat UI holds them and requires an explicit
// Confirm tap first.

import { store, fmtElapsed, fmtWhen, type BriefMeta } from "../store";
import { exportBackup, importBackup } from "../backup";
import { invoke } from "../tauri-web";
import { saveCopyNow, importFromFile } from "../localFolder";
const LOCAL_SLUG = "scout";
import { cloudBackupSave, cloudBackupLoad, cloudBackupStatus, cloudStoreSave } from "../brain";
import { addInstruction } from "./brain";
import { runResearch, BrainError } from "../brain";
import { libraryAdd, libraryList, libraryRemove, libraryMove, libraryMerge } from "../library";

// A compact brief row rendered inline in a chat bubble (search results).
export interface FilterRow {
  id: string;
  question: string;
  meta: string;
}

export type ChatAction =
  | { kind: "none"; message?: string }
  | { kind: "research"; question: string } // reuses the existing runResearch pipeline (the ONE billed path)
  | { kind: "filter"; query: string; label?: string } // local search over saved briefs
  | { kind: "summarize" } // local history stats
  | { kind: "delete_brief"; id: string; label?: string } // UI confirms before this reaches executeAction
  // Edits the customer's OWN label/notes only. The question, the answer and the sources are
  // untouched — a brief must keep answering the question it was actually asked. Not destructive,
  // so no confirm gate.
  | { kind: "update_brief"; id: string; title?: string; notes?: string; label?: string }
  | { kind: "clear_history" } // UI confirms before this reaches executeAction
  | { kind: "backup_save" }
  | { kind: "backup_status" }
  | { kind: "backup_restore"; provider?: "github" | "gdrive" } // UI confirms before executeAction (replaces all data)
  | { kind: "save_local_copy" }
  | { kind: "import_file" }
  | { kind: "save_to_cloud"; contentKind: "conversation" | "note"; note?: string; destination?: "github" | "gdrive" }
  | { kind: "remember"; instruction: string }
  // ── ARTIFACT TOOLS — the customer's library (fleet-wide) ────────────────────
  | { kind: "library_save"; title: string; itemKind: "lesson" | "plan" | "note"; content: string; folder?: string }
  | { kind: "library_list"; folder?: string }
  | { kind: "library_remove"; id: string; label?: string }   // UI confirms first
  | { kind: "library_move"; id: string; folder: string; label?: string }
  | { kind: "library_merge"; ids: string[]; title: string; label?: string };  // UI confirms first

export interface ActionResult {
  ok: boolean;
  message: string;    // shown back in the chat
  changed?: boolean;  // history changed → the main list should refresh
  rows?: FilterRow[]; // search matches to render inline
}

function rowMeta(m: BriefMeta): string {
  const bits: string[] = [];
  if (m.status === "running") bits.push("researching…");
  else if (m.status === "failed") bits.push("didn't finish");
  else {
    bits.push(`${m.source_count} source${m.source_count === 1 ? "" : "s"}`);
    if (m.elapsed_ms > 0) bits.push(fmtElapsed(m.elapsed_ms));
  }
  bits.push(fmtWhen(m.created_at));
  return bits.filter(Boolean).join(" · ");
}

export async function executeAction(a: ChatAction, ctx?: { transcript?: string }): Promise<ActionResult> {
  try {
    switch (a.kind) {
      case "none":
        return { ok: true, message: a.message ?? "" };

      case "research": {
        const question = a.question.trim();
        if (!question) return { ok: true, message: 'What should I research? Try "research the evidence on creatine for cognition".' };
        // Open the run in on-device history immediately (same as Home's runResearchFlow).
        let id: string;
        try {
          id = await store.beginBrief(question);
        } catch {
          return { ok: false, message: "Couldn't start — your device storage is unavailable." };
        }
        const started = Date.now();
        try {
          // The app's ONE live pipeline: plan → gather ×N → synthesize (each step one billed POST).
          const brief = await runResearch(question, () => {});
          const elapsed = Date.now() - started;
          await store.completeBrief({
            id,
            summary: brief.summary,
            body_json: JSON.stringify({ summary: brief.summary, key_points: brief.key_points, sources: brief.sources }),
            sources: brief.sources.map((s) => ({ ordinal: s.ordinal, title: s.title, url: s.url, snippet: s.snippet })),
            key_points_text: brief.key_points.map((k) => k.text).join("\n"),
            elapsed_ms: elapsed,
          });
          const kp = brief.key_points.length;
          const n = brief.sources.length;
          const body = brief.summary.trim() || "Done.";
          return {
            ok: true,
            changed: true,
            message: `${body}\n\n${kp} key point${kp === 1 ? "" : "s"} · ${n} source${n === 1 ? "" : "s"} — saved to your research history.`,
          };
        } catch (e) {
          // Keep the failed attempt in history (retryable), and refresh the list.
          try { await store.failBrief(id); } catch { /* ignore */ }
          if (e instanceof BrainError) return { ok: false, changed: true, message: e.message };
          return { ok: false, changed: true, message: `Research failed: ${e instanceof Error ? e.message : String(e)}` };
        }
      }

      case "filter": {
        // Local FTS5 over saved briefs — no brain call. Empty query → newest briefs.
        const rows = await store.searchHistory(a.query, 200);
        const forLabel = a.label ? ` for "${a.label}"` : "";
        const rendered: FilterRow[] = rows.slice(0, 50).map((m) => ({ id: m.id, question: m.question, meta: rowMeta(m) }));
        return {
          ok: true,
          message: `${rows.length} brief${rows.length !== 1 ? "s" : ""}${forLabel}` + (rows.length > 50 ? " (showing first 50)" : ""),
          rows: rendered,
        };
      }

      case "summarize": {
        const all = await store.listBriefs(50000);
        if (all.length === 0) return { ok: true, message: "No research saved yet. Ask me to research something to get started." };
        const done = all.filter((b) => b.status === "done");
        const running = all.filter((b) => b.status === "running").length;
        const failed = all.filter((b) => b.status === "failed").length;
        const totalSources = done.reduce((n, b) => n + (b.source_count || 0), 0);
        const parts = [
          `${done.length} completed brief${done.length === 1 ? "" : "s"}`,
          `${totalSources} source${totalSources === 1 ? "" : "s"} cited`,
        ];
        if (running) parts.push(`${running} in progress`);
        if (failed) parts.push(`${failed} unfinished`);
        return { ok: true, message: parts.join(" · ") + "." };
      }

      case "delete_brief":
        await store.deleteBrief(a.id);
        return { ok: true, message: `Deleted "${a.label ?? "that brief"}" from this device.`, changed: true };

      case "update_brief": {
        const patch: { title?: string; notes?: string } = {};
        if (a.title !== undefined) patch.title = a.title;
        if (a.notes !== undefined) patch.notes = a.notes;
        if (!Object.keys(patch).length) return { ok: false, message: "Tell me what to change — the title, or your notes." };
        await store.updateBrief(a.id, patch);
        const what = patch.title !== undefined && patch.notes !== undefined ? "title and notes"
          : patch.title !== undefined ? "title" : "notes";
        return { ok: true, message: `Updated the ${what} on "${a.label ?? "that brief"}". The question and its sources are unchanged.`, changed: true };
      }

      case "clear_history":
        await store.clearHistory();
        return { ok: true, message: "Cleared your research history on this device.", changed: true };

      case "backup_save": {
        let provider: string;
        try {
          ({ provider } = await cloudBackupSave(JSON.parse(await exportBackup())));
        } catch (e) {
          // A backup past the standard path's size limit → the device-direct GitHub
          // save (any size, straight from this device, never through Oriora). Chat can DO this
          // one itself when GitHub is already connected in the backup menu; it can't run the
          // one-time OAuth login, so when not connected it points there by name instead.
          if (e instanceof BrainError && e.code === "too_large") {
            const connected = await invoke<boolean>("has_github_restore_token").catch(() => false);
            if (connected) {
              await invoke("backup_to_github_token");
              return { ok: true, message: "Your backup was too large for the standard path, so I saved it straight from this device to your GitHub — any size works that way, and it never passes through Oriora." };
            }
            return { ok: false, message: 'This backup is too large for the standard path. One-time fix: open the backup menu (☁ icon, top right) → "Log in with GitHub" — after that, large backups save directly from this device, and I can run them for you from chat.' };
          }
          throw e;
        }
        const name = provider === "github" ? "GitHub" : provider === "gdrive" ? "Google Drive" : "cloud";
        return { ok: true, message: "Backed up your whole collection to your " + name + " — restore it on any device, any time." };
      }

      case "backup_status": {
        // The RESTORE STORY in words — same ground truth the panel's status line shows.
        const s = await cloudBackupStatus();
        if (s.connected.length === 0) {
          const deviceGh = await invoke<boolean>("has_github_restore_token").catch(() => false);
          if (deviceGh) {
            return { ok: true, message: "GitHub is logged in on this device (backup menu), so backups save and restore directly from here — any size, never passing through Oriora. Optionally, connecting a cloud at orioralabs.com → Settings → Connections adds the everyday path all your Oriora apps share." };
          }
          return { ok: true, message: 'Nothing is connected yet. Two options: connect GitHub or Google Drive once at orioralabs.com → Settings → Connections (all your Oriora apps back up there), or open the backup menu here (☁ icon) → "Log in with GitHub" — that one stays on this device only, and handles any size.' };
        }
        const name = (p: string) => (p === "gdrive" ? "Google Drive" : "GitHub");
        const when = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }) : null);
        const lines: string[] = [];
        const w = s.writes_to;
        if (w) {
          const slot = s.slots[w];
          lines.push(`Your backups write to ${name(w)} (connected in your Oriora account)` +
            (slot?.backup_exists ? ` — last backup ${when(slot.updated_at)}.` : " — no backup saved yet."));
        }
        for (const p of s.connected.filter((c) => c !== w)) {
          const slot = s.slots[p];
          lines.push(`${name(p)} is also connected but not used for new backups (${name(w!)} takes priority)` +
            (slot?.backup_exists ? ` — it still holds an older backup from ${when(slot.updated_at)}.` : "."));
        }
        const deviceGh = await invoke<boolean>("has_github_restore_token").catch(() => false);
        if (deviceGh) {
          lines.push("GitHub is also logged in on this device, so large backups save and restore directly from here — any size, never through Oriora.");
        }
        lines.push('Say "restore my research" and I\'ll read the backup back in — or name a source, like "restore from Drive".');
        return { ok: true, message: lines.join("\n") };
      }

      case "backup_restore": {
        const src = a.provider ? (a.provider === "gdrive" ? "Google Drive" : "GitHub") : "cloud";
        let st;
        try {
          st = await cloudBackupLoad(a.provider);
        } catch (e) {
          // Large-backup handoff: if GitHub is connected on this device, chat pulls it directly
          // itself (the user already confirmed the REPLACE before this executes); it can't run the
          // one-time OAuth login, so when not connected it points at the button by name instead.
          if (e instanceof BrainError && e.code === "unavailable") {
            const connected = await invoke<boolean>("has_github_restore_token").catch(() => false);
            if (connected && (!a.provider || a.provider === "github")) {
              await invoke("restore_from_github_token");
              return { ok: true, message: "Your backup was too large for the standard path, so I pulled it straight from your GitHub onto this device — your data is back.", changed: true };
            }
            return { ok: false, message: `Reading your ${src} backup through Oriora didn't work — if your backup is large, use the backup menu (☁ icon, top right) → "Log in with GitHub", which backs up and restores any size directly on this device.` };
          }
          throw e;
        }
        if (!st.connected) return { ok: false, message: "Connect GitHub or Google Drive in your Oriora account first (Settings → Service connections)." };
        if (!st.data) return { ok: false, message: a.provider ? `No backup found in your ${src} yet.` : 'No cloud backup found yet — say "back up" first.' };
        await importBackup(JSON.stringify(st.data));
        return { ok: true, message: `Restored from your ${st.provider === "gdrive" ? "Google Drive" : "GitHub"} backup.`, changed: true };
      }

      case "save_local_copy": {
        const saved = await saveCopyNow(LOCAL_SLUG);
        return saved
          ? { ok: true, message: "Saved a copy of your data to the folder you picked — it's yours to keep or move." }
          : { ok: true, message: "Cancelled — no copy saved." };
      }

      case "import_file": {
        const json = await importFromFile();
        if (json === null) return { ok: true, message: "No file chosen — nothing imported." };
        await importBackup(json);
        return { ok: true, message: "Imported your data from the file — this device now matches that backup.", changed: true };
      }

      case "save_to_cloud": {
        // Save a single note or the whole conversation to the customer's OWN cloud (their GitHub/Drive),
        // distinct from backup_save (the whole research history). Oriora retains nothing.
        const content = a.contentKind === "conversation" ? (ctx?.transcript ?? "") : (a.note ?? "");
        if (!content.trim()) {
          return { ok: false, message: a.contentKind === "conversation" ? "There's nothing to save yet — chat with me a bit first, then ask again." : "Tell me what to save and I'll store it." };
        }
        const { provider } = await cloudStoreSave(content, { name: a.contentKind, kind: a.contentKind, destination: a.destination });
        const where = provider === "github" ? "GitHub" : provider === "gdrive" ? "Google Drive" : "your cloud";
        const what = a.contentKind === "conversation" ? "our conversation" : "your note";
        return { ok: true, message: `Saved ${what} to your ${where} — it's yours to keep.` };
      }

      case "remember": {
        addInstruction(a.instruction);
        return { ok: true, message: "Noted — from now on: " + a.instruction };
      }

      // ── ARTIFACT TOOLS (the library) — device-only, fleet-wide ──────────────
      case "library_save": {
        const _item = libraryAdd(a.title, a.itemKind, a.content, a.folder ?? "");
        return { ok: true, message: `Saved "${_item.title}" to your library${_item.folder ? ` (folder: ${_item.folder})` : ""} — on this device, yours.`, changed: true };
      }
      case "library_list": {
        const _items = libraryList(a.folder);
        if (!_items.length) return { ok: true, message: a.folder ? `Nothing in "${a.folder}" yet.` : "Your library is empty — ask me to write you something and I'll keep it here." };
        return { ok: true, message: (a.folder ? `In "${a.folder}":\n` : "Your library:\n") + _items.map((i) => `• ${i.title} — ${i.kind}${i.folder ? ` · ${i.folder}` : ""}`).join("\n") };
      }
      case "library_remove":
        return libraryRemove(a.id)
          ? { ok: true, message: `Removed ${a.label ?? "it"} from your library.`, changed: true }
          : { ok: false, message: "That item isn't in your library any more." };
      case "library_move":
        return libraryMove(a.id, a.folder)
          ? { ok: true, message: `Moved ${a.label ?? "it"} to "${a.folder}".`, changed: true }
          : { ok: false, message: "That item isn't in your library any more." };
      case "library_merge": {
        const _merged = libraryMerge(a.ids, a.title);
        return _merged
          ? { ok: true, message: `Merged into "${_merged.title}" — the originals are folded in.`, changed: true }
          : { ok: false, message: "I need at least two existing items to merge." };
      }


      default:
        return { ok: false, message: "I didn't catch that — try again?" };
    }
  } catch (e) {
    if (e instanceof BrainError) return { ok: false, message: e.message };
    return { ok: false, message: `That didn't work: ${e instanceof Error ? e.message : String(e)}` };
  }
}
